import { Filter, List, UserPlus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { AFFILIATE_CONTRACT_DRAFT_FR } from '../lib/affiliateContractDraftFr'
import { api } from '../lib/api'
import { useAppLanguage } from '../i18n'

type ProgramSettings = {
  enabled: boolean
  commission_rate_bps: number
  recurring_commissions: boolean
  max_commission_months: number
  min_invoice_cents: number
  excluded_plan_slugs: string | null
  referral_cookie_name: string
  referral_cookie_max_age_seconds: number
  stripe_connect_payouts_enabled: boolean
  stripe_connect_default_country: string
  default_payout_currency: string
  payable_after_referral_days: number
  affiliate_contract_version: string
  affiliate_contract_markdown: string | null
}

type Commission = {
  public_id: string
  referrer_user_id: number
  referred_user_id: number
  stripe_invoice_id: string
  base_amount_cents: number
  commission_amount_cents: number
  currency: string
  rate_bps: number
  plan_slug: string | null
  status: string
  payable_at: string | null
  payout_reference: string | null
  stripe_transfer_id: string | null
  paid_at: string | null
  created_at: string
}

type Referrer = {
  user_id: number
  email: string | null
  public_code: string
  status: string
  stripe_connect_account_id: string | null
  created_at: string | null
  referrals_count: number
  commissions_payable_cents: number
  commissions_paid_cents: number
}

type Attribution = {
  id: number
  referrer_user_id: number
  referred_user_id: number
  referrer_email?: string | null
  referred_email?: string | null
  source: string
  ref_code_used: string | null
  created_at: string | null
}

type Tab = 'settings' | 'referrers' | 'attributions' | 'commissions'

const LIMIT = 50

export default function AdminAffiliatePage() {
  const { t } = useAppLanguage()
  const a = t.adminAffiliate
  const [tab, setTab] = useState<Tab>('settings')

  const [programDraft, setProgramDraft] = useState<ProgramSettings | null>(null)
  const [savingProgram, setSavingProgram] = useState(false)

  const [referrers, setReferrers] = useState<Referrer[]>([])
  const [refTotal, setRefTotal] = useState(0)
  const [refSkip, setRefSkip] = useState(0)
  const [refStatus, setRefStatus] = useState('')
  const [refEmail, setRefEmail] = useState('')
  const [refBusy, setRefBusy] = useState<string | null>(null)

  const [attrs, setAttrs] = useState<Attribution[]>([])
  const [attrTotal, setAttrTotal] = useState(0)
  const [attrSkip, setAttrSkip] = useState(0)
  const [attrRefUid, setAttrRefUid] = useState('')
  const [attrReUid, setAttrReUid] = useState('')
  const [attrFilterReferrerEmail, setAttrFilterReferrerEmail] = useState('')
  const [attrFilterReferredEmail, setAttrFilterReferredEmail] = useState('')
  const [newAttrRef, setNewAttrRef] = useState('')
  const [newAttrRe, setNewAttrRe] = useState('')
  const [newAttrReferrerEmail, setNewAttrReferrerEmail] = useState('')
  const [newAttrReferredEmail, setNewAttrReferredEmail] = useState('')

  const [rows, setRows] = useState<Commission[]>([])
  const [commTotal, setCommTotal] = useState(0)
  const [commSkip, setCommSkip] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [commReferrerId, setCommReferrerId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [marking, setMarking] = useState<string | null>(null)
  const [payoutRef, setPayoutRef] = useState<Record<string, string>>({})
  const [stripeTransfer, setStripeTransfer] = useState<Record<string, boolean>>({})

  const loadProgram = useCallback(async () => {
    const { data } = await api.get<ProgramSettings>('/admin/affiliate/program-settings')
    const md = data.affiliate_contract_markdown
    const hasSavedContract = md != null && String(md).trim() !== ''
    setProgramDraft({
      ...data,
      affiliate_contract_markdown: hasSavedContract ? md : AFFILIATE_CONTRACT_DRAFT_FR,
    })
  }, [])

  const loadReferrers = useCallback(async () => {
    setErr(null)
    setLoading(true)
    try {
      const params: Record<string, string | number> = { skip: refSkip, limit: LIMIT }
      if (refStatus) params.status = refStatus
      if (refEmail.trim()) params.email_search = refEmail.trim()
      const { data } = await api.get<{ items: Referrer[]; total: number }>('/admin/affiliate/referrers', {
        params,
      })
      setReferrers(data.items)
      setRefTotal(data.total)
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    } finally {
      setLoading(false)
    }
  }, [refSkip, refStatus, refEmail])

  const fetchAttributionsList = useCallback(
    async (
      skip: number,
      filters: {
        referrerUserId: string
        referredUserId: string
        referrerEmail: string
        referredEmail: string
      },
    ) => {
      setErr(null)
      setLoading(true)
      try {
        const params: Record<string, string | number> = { skip, limit: LIMIT }
        const rId = filters.referrerUserId.trim()
        const dId = filters.referredUserId.trim()
        if (rId) {
          const n = parseInt(rId, 10)
          if (Number.isFinite(n)) params.referrer_user_id = n
        }
        if (dId) {
          const n = parseInt(dId, 10)
          if (Number.isFinite(n)) params.referred_user_id = n
        }
        const rEm = filters.referrerEmail.trim()
        const dEm = filters.referredEmail.trim()
        if (rEm) params.referrer_email = rEm
        if (dEm) params.referred_email = dEm
        const { data } = await api.get<{ items: Attribution[]; total: number }>(
          '/admin/affiliate/attributions',
          { params },
        )
        setAttrs(data.items)
        setAttrTotal(data.total)
      } catch (e: unknown) {
        const ax = e as { response?: { data?: { detail?: unknown } } }
        const d = ax.response?.data?.detail
        const msg =
          typeof d === 'string'
            ? d
            : Array.isArray(d)
              ? d.map((x: { msg?: string }) => x?.msg || '').filter(Boolean).join(', ')
              : 'Error'
        setErr(msg || 'Error')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const loadAttrs = useCallback(() => {
    return fetchAttributionsList(attrSkip, {
      referrerUserId: attrRefUid,
      referredUserId: attrReUid,
      referrerEmail: attrFilterReferrerEmail,
      referredEmail: attrFilterReferredEmail,
    })
  }, [
    attrSkip,
    attrRefUid,
    attrReUid,
    attrFilterReferrerEmail,
    attrFilterReferredEmail,
    fetchAttributionsList,
  ])

  const loadCommissions = useCallback(async () => {
    setErr(null)
    setLoading(true)
    try {
      const params: Record<string, string | number> = { skip: commSkip, limit: LIMIT }
      if (statusFilter) params.status = statusFilter
      if (commReferrerId.trim()) params.referrer_user_id = parseInt(commReferrerId, 10)
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      const { data } = await api.get<{ items: Commission[]; total: number }>(
        '/admin/affiliate/commissions',
        { params },
      )
      setRows(data.items)
      setCommTotal(data.total)
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    } finally {
      setLoading(false)
    }
  }, [commSkip, statusFilter, commReferrerId, dateFrom, dateTo])

  useEffect(() => {
    if (tab === 'settings') {
      setLoading(true)
      loadProgram()
        .catch(() => setErr('Failed to load settings'))
        .finally(() => setLoading(false))
    }
  }, [tab, loadProgram])

  useEffect(() => {
    if (tab === 'referrers') loadReferrers()
  }, [tab, loadReferrers])

  useEffect(() => {
    if (tab === 'attributions') loadAttrs()
  }, [tab, loadAttrs])

  useEffect(() => {
    if (tab === 'commissions') loadCommissions()
  }, [tab, loadCommissions])

  const saveProgram = async () => {
    if (!programDraft) return
    setSavingProgram(true)
    setErr(null)
    try {
      const { data } = await api.patch<ProgramSettings>('/admin/affiliate/program-settings', programDraft)
      setProgramDraft(data)
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Save failed')
    } finally {
      setSavingProgram(false)
    }
  }

  const exportCsv = async () => {
    try {
      const params: Record<string, string> = {}
      if (statusFilter) params.status = statusFilter
      if (commReferrerId.trim()) params.referrer_user_id = commReferrerId.trim()
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      const res = await api.get('/admin/affiliate/commissions/export.csv', {
        params,
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(res.data)
      const el = document.createElement('a')
      el.href = url
      el.download = 'affiliate_commissions.csv'
      el.click()
      window.URL.revokeObjectURL(url)
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Export failed')
    }
  }

  const markPaid = async (publicId: string) => {
    const ref = (payoutRef[publicId] || '').trim()
    const doStripe = !!stripeTransfer[publicId]
    if (!doStripe && !ref) return
    setMarking(publicId)
    try {
      await api.post(`/admin/affiliate/commissions/${publicId}/mark-paid`, {
        payout_reference: ref || null,
        execute_stripe_transfer: doStripe,
      })
      setPayoutRef((p) => ({ ...p, [publicId]: '' }))
      setStripeTransfer((p) => ({ ...p, [publicId]: false }))
      await loadCommissions()
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    } finally {
      setMarking(null)
    }
  }

  const patchReferrer = async (userId: number, body: { status?: string; rotate_public_code?: boolean }) => {
    setRefBusy(String(userId))
    setErr(null)
    try {
      await api.patch(`/admin/affiliate/referrers/${userId}`, body)
      await loadReferrers()
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    } finally {
      setRefBusy(null)
    }
  }

  const openConnectLink = async (userId: number) => {
    const origin = window.location.origin
    setRefBusy(`c${userId}`)
    setErr(null)
    try {
      const { data } = await api.post<{ url: string }>(
        `/admin/affiliate/referrers/${userId}/connect/onboarding-link`,
        {
          refresh_url: `${origin}/admin/affiliate`,
          return_url: `${origin}/admin/affiliate`,
        },
      )
      window.location.href = data.url
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Connect error')
      setRefBusy(null)
    }
  }

  const createAttr = async () => {
    const r = parseInt(newAttrRef, 10)
    const d = parseInt(newAttrRe, 10)
    const rEm = newAttrReferrerEmail.trim()
    const dEm = newAttrReferredEmail.trim()
    const hasRef = Number.isFinite(r) || rEm.length > 0
    const hasRe = Number.isFinite(d) || dEm.length > 0
    if (!hasRef || !hasRe) {
      setErr(a.attributionsCreateNeedBoth)
      return
    }
    setErr(null)
    try {
      const body: Record<string, unknown> = {}
      if (Number.isFinite(r)) body.referrer_user_id = r
      if (rEm) body.referrer_email = rEm
      if (Number.isFinite(d)) body.referred_user_id = d
      if (dEm) body.referred_email = dEm
      await api.post('/admin/affiliate/attributions', body)
      setNewAttrRef('')
      setNewAttrRe('')
      setNewAttrReferrerEmail('')
      setNewAttrReferredEmail('')
      await loadAttrs()
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: unknown } } }
      const det = ax.response?.data?.detail
      const msg = typeof det === 'string' ? det : 'Error'
      setErr(msg)
    }
  }

  const deleteAttr = async (id: number) => {
    if (!window.confirm(t.common.irreversibleAction)) return
    try {
      await api.delete(`/admin/affiliate/attributions/${id}`)
      await loadAttrs()
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    }
  }

  const fmt = (cents: number, cur: string) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: cur || 'EUR' }).format(cents / 100)

  const tabBtn = (id: Tab, label: string) => (
    <button
      type="button"
      key={id}
      onClick={() => setTab(id)}
      className={`rounded-full px-4 py-2 text-sm font-medium transition ${
        tab === id
          ? 'bg-emerald-600 text-white'
          : 'border border-emerald-200 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-200 dark:hover:bg-emerald-950/40'
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div className="relative mx-auto max-w-[1600px] px-6 py-8">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">{a.title}</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{a.subtitle}</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {tabBtn('settings', a.tabSettings)}
          {tabBtn('referrers', a.tabReferrers)}
          {tabBtn('attributions', a.tabAttributions)}
          {tabBtn('commissions', a.tabCommissions)}
        </div>

        {err && <p className="mt-4 text-sm text-red-600">{err}</p>}

        {tab === 'settings' && loading && !programDraft && (
          <p className="mt-8 text-zinc-500">{t.common.loading}</p>
        )}

        {tab === 'settings' && programDraft && (
          <div className="mt-8 rounded-3xl border border-emerald-100/70 bg-white/90 p-6 shadow-lg dark:border-emerald-900/50 dark:bg-zinc-950/90">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
              <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={programDraft.enabled}
                onChange={(e) => setProgramDraft({ ...programDraft, enabled: e.target.checked })}
              />
              {a.programEnabled}
            </label>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.commissionRateBps}</label>
              <input
                type="number"
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.commission_rate_bps}
                onChange={(e) =>
                  setProgramDraft({ ...programDraft, commission_rate_bps: parseInt(e.target.value, 10) || 0 })
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={programDraft.recurring_commissions}
                onChange={(e) =>
                  setProgramDraft({ ...programDraft, recurring_commissions: e.target.checked })
                }
              />
              {a.recurringCommissions}
            </label>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.maxCommissionMonths}</label>
              <input
                type="number"
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.max_commission_months}
                onChange={(e) =>
                  setProgramDraft({
                    ...programDraft,
                    max_commission_months: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.minInvoiceCents}</label>
              <input
                type="number"
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.min_invoice_cents}
                onChange={(e) =>
                  setProgramDraft({
                    ...programDraft,
                    min_invoice_cents: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.payableAfterReferralDays}</label>
              <input
                type="number"
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.payable_after_referral_days}
                onChange={(e) =>
                  setProgramDraft({
                    ...programDraft,
                    payable_after_referral_days: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
              <p className="mt-1 text-xs text-zinc-500">{a.payableAfterReferralHint}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.excludedPlanSlugs}</label>
              <input
                type="text"
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.excluded_plan_slugs || ''}
                onChange={(e) => setProgramDraft({ ...programDraft, excluded_plan_slugs: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.cookieName}</label>
              <input
                type="text"
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.referral_cookie_name}
                onChange={(e) =>
                  setProgramDraft({ ...programDraft, referral_cookie_name: e.target.value })
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.cookieMaxAge}</label>
              <input
                type="number"
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.referral_cookie_max_age_seconds}
                onChange={(e) =>
                  setProgramDraft({
                    ...programDraft,
                    referral_cookie_max_age_seconds: parseInt(e.target.value, 10) || 60,
                  })
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={programDraft.stripe_connect_payouts_enabled}
                onChange={(e) =>
                  setProgramDraft({ ...programDraft, stripe_connect_payouts_enabled: e.target.checked })
                }
              />
              {a.stripeConnectPayouts}
            </label>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.stripeConnectCountry}</label>
              <input
                type="text"
                maxLength={2}
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm uppercase dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.stripe_connect_default_country}
                onChange={(e) =>
                  setProgramDraft({
                    ...programDraft,
                    stripe_connect_default_country: e.target.value.toUpperCase().slice(0, 2),
                  })
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500">{a.defaultPayoutCurrency}</label>
              <input
                type="text"
                maxLength={3}
                className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm uppercase dark:border-zinc-700 dark:bg-zinc-900"
                value={programDraft.default_payout_currency}
                onChange={(e) =>
                  setProgramDraft({
                    ...programDraft,
                    default_payout_currency: e.target.value.toUpperCase().slice(0, 3),
                  })
                }
              />
            </div>
              </div>

              <div className="flex min-h-0 flex-col rounded-2xl border border-emerald-200/80 bg-emerald-50/40 p-4 dark:border-emerald-800/60 dark:bg-emerald-950/30">
                <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{a.contractSection}</p>
                <div className="mt-3 shrink-0">
                  <label className="text-xs font-medium text-zinc-500">{a.contractVersion}</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                    value={programDraft.affiliate_contract_version}
                    onChange={(e) =>
                      setProgramDraft({ ...programDraft, affiliate_contract_version: e.target.value })
                    }
                  />
                  <p className="mt-1 text-xs text-zinc-500">{a.contractVersionHint}</p>
                </div>
                <div className="mt-4 flex min-h-0 flex-1 flex-col">
                  <label className="shrink-0 text-xs font-medium text-zinc-500">{a.contractMarkdown}</label>
                  <textarea
                    rows={22}
                    className="mt-1 min-h-[min(70vh,36rem)] w-full flex-1 resize-y rounded-2xl border border-zinc-200 px-3 py-3 font-mono text-sm leading-relaxed dark:border-zinc-700 dark:bg-zinc-900"
                    placeholder={a.contractMarkdownPlaceholder}
                    value={programDraft.affiliate_contract_markdown || ''}
                    onChange={(e) =>
                      setProgramDraft({ ...programDraft, affiliate_contract_markdown: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-emerald-100 pt-6 dark:border-emerald-900/40">
              <button
                type="button"
                disabled={savingProgram}
                onClick={() => void saveProgram()}
                className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {savingProgram ? t.common.loading : t.common.save}
              </button>
            </div>
          </div>
        )}

        {tab === 'referrers' && (
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder={a.emailSearch}
                value={refEmail}
                onChange={(e) => setRefEmail(e.target.value)}
                className="rounded-full border border-emerald-200 bg-white px-3 py-2 text-sm dark:border-emerald-800 dark:bg-zinc-900"
              />
              <select
                value={refStatus}
                onChange={(e) => setRefStatus(e.target.value)}
                className="rounded-full border border-emerald-200 bg-white px-3 py-2 text-sm dark:border-emerald-800 dark:bg-zinc-900"
              >
                <option value="">{a.allStatuses}</option>
                <option value="active">{t.common.active}</option>
                <option value="suspended">{a.suspended}</option>
              </select>
              <button
                type="button"
                onClick={() => {
                  setRefSkip(0)
                  void loadReferrers()
                }}
                className="rounded-full border border-emerald-200 px-4 py-2 text-sm dark:border-emerald-700"
              >
                {a.refresh}
              </button>
            </div>
            {loading ? (
              <p className="text-zinc-500">{t.common.loading}</p>
            ) : (
              <div className="overflow-x-auto rounded-3xl border border-emerald-100/70 bg-white/90 dark:border-emerald-900/50 dark:bg-zinc-950/90">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="border-b border-emerald-100 dark:border-emerald-900/50">
                    <tr>
                      <th className="px-4 py-3">{a.emailColumn}</th>
                      <th className="px-4 py-3">{a.publicCode}</th>
                      <th className="px-4 py-3">{t.common.status}</th>
                      <th className="px-4 py-3">{a.referralsCount}</th>
                      <th className="px-4 py-3">{a.payableCents}</th>
                      <th className="px-4 py-3">{a.paidCents}</th>
                      <th className="px-4 py-3">{t.common.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrers.map((r) => (
                      <tr key={r.user_id} className="border-b border-zinc-100 dark:border-zinc-800">
                        <td className="px-4 py-3">{r.email || r.user_id}</td>
                        <td className="px-4 py-3 font-mono text-xs">{r.public_code}</td>
                        <td className="px-4 py-3">{r.status}</td>
                        <td className="px-4 py-3">{r.referrals_count}</td>
                        <td className="px-4 py-3">{r.commissions_payable_cents}</td>
                        <td className="px-4 py-3">{r.commissions_paid_cents}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {r.status === 'active' ? (
                              <button
                                type="button"
                                disabled={refBusy === String(r.user_id)}
                                onClick={() => void patchReferrer(r.user_id, { status: 'suspended' })}
                                className="rounded-full border border-zinc-300 px-2 py-1 text-xs dark:border-zinc-600"
                              >
                                {a.suspend}
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={refBusy === String(r.user_id)}
                                onClick={() => void patchReferrer(r.user_id, { status: 'active' })}
                                className="rounded-full border border-zinc-300 px-2 py-1 text-xs dark:border-zinc-600"
                              >
                                {a.activate}
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={refBusy === String(r.user_id)}
                              onClick={() => void patchReferrer(r.user_id, { rotate_public_code: true })}
                              className="rounded-full border border-zinc-300 px-2 py-1 text-xs dark:border-zinc-600"
                            >
                              {a.rotateCode}
                            </button>
                            <button
                              type="button"
                              disabled={refBusy === `c${r.user_id}`}
                              onClick={() => void openConnectLink(r.user_id)}
                              className="rounded-full bg-emerald-600 px-2 py-1 text-xs font-medium text-white"
                            >
                              {a.stripeOnboarding}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <span>
                {a.total}: {refTotal}
              </span>
              <button
                type="button"
                disabled={refSkip < LIMIT}
                onClick={() => setRefSkip((s) => Math.max(0, s - LIMIT))}
                className="rounded-full border px-3 py-1 disabled:opacity-40"
              >
                {a.prev}
              </button>
              <button
                type="button"
                disabled={refSkip + LIMIT >= refTotal}
                onClick={() => setRefSkip((s) => s + LIMIT)}
                className="rounded-full border px-3 py-1 disabled:opacity-40"
              >
                {a.next}
              </button>
            </div>
          </div>
        )}

        {tab === 'attributions' && (
          <div className="mt-6 space-y-6">
            <div className="overflow-hidden rounded-3xl border border-emerald-100/70 bg-white/90 shadow-lg dark:border-emerald-900/50 dark:bg-zinc-950/90">
              <form
                className="border-b border-emerald-100/90 bg-gradient-to-r from-emerald-50/90 to-white p-5 dark:border-emerald-900/50 dark:from-emerald-950/40 dark:to-zinc-950/90"
                onSubmit={(e) => {
                  e.preventDefault()
                  void createAttr()
                }}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                    <UserPlus className="h-4 w-4" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                      {a.attributionsCreateTitle}
                    </h2>
                    <p className="mt-1 max-w-3xl text-xs text-zinc-600 dark:text-zinc-400">
                      {a.attributionsCreateHint}
                    </p>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="attr-new-referrer" className="text-xs font-medium text-zinc-500">
                          {a.newReferrerId}
                        </label>
                        <input
                          id="attr-new-referrer"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          placeholder="123"
                          value={newAttrRef}
                          onChange={(e) => setNewAttrRef(e.target.value)}
                          className="mt-1 w-full rounded-full border border-emerald-200/80 bg-white px-3 py-2 text-sm tabular-nums dark:border-emerald-800 dark:bg-zinc-900"
                        />
                      </div>
                      <div>
                        <label htmlFor="attr-new-referrer-email" className="text-xs font-medium text-zinc-500">
                          {a.newReferrerEmail}
                        </label>
                        <input
                          id="attr-new-referrer-email"
                          type="email"
                          autoComplete="off"
                          placeholder="parrain@exemple.com"
                          value={newAttrReferrerEmail}
                          onChange={(e) => setNewAttrReferrerEmail(e.target.value)}
                          className="mt-1 w-full rounded-full border border-emerald-200/80 bg-white px-3 py-2 text-sm dark:border-emerald-800 dark:bg-zinc-900"
                        />
                      </div>
                      <div>
                        <label htmlFor="attr-new-referred" className="text-xs font-medium text-zinc-500">
                          {a.newReferredId}
                        </label>
                        <input
                          id="attr-new-referred"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          placeholder="456"
                          value={newAttrRe}
                          onChange={(e) => setNewAttrRe(e.target.value)}
                          className="mt-1 w-full rounded-full border border-emerald-200/80 bg-white px-3 py-2 text-sm tabular-nums dark:border-emerald-800 dark:bg-zinc-900"
                        />
                      </div>
                      <div>
                        <label htmlFor="attr-new-referred-email" className="text-xs font-medium text-zinc-500">
                          {a.newReferredEmail}
                        </label>
                        <input
                          id="attr-new-referred-email"
                          type="email"
                          autoComplete="off"
                          placeholder="filleul@exemple.com"
                          value={newAttrReferredEmail}
                          onChange={(e) => setNewAttrReferredEmail(e.target.value)}
                          className="mt-1 w-full rounded-full border border-emerald-200/80 bg-white px-3 py-2 text-sm dark:border-emerald-800 dark:bg-zinc-900"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="submit"
                        className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500"
                      >
                        {a.createAttribution}
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              <div className="p-5">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-200/80 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                    <Filter className="h-4 w-4" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                      {a.attributionsFilterTitle}
                    </h2>
                    <p className="mt-1 max-w-3xl text-xs text-zinc-600 dark:text-zinc-400">
                      {a.attributionsFilterHint}
                    </p>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="attr-filter-referrer" className="text-xs font-medium text-zinc-500">
                          {a.filterReferrerId}
                        </label>
                        <input
                          id="attr-filter-referrer"
                          type="number"
                          inputMode="numeric"
                          placeholder="—"
                          value={attrRefUid}
                          onChange={(e) => setAttrRefUid(e.target.value)}
                          className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm tabular-nums dark:border-zinc-700 dark:bg-zinc-900"
                        />
                      </div>
                      <div>
                        <label htmlFor="attr-filter-referrer-email" className="text-xs font-medium text-zinc-500">
                          {a.filterReferrerEmail}
                        </label>
                        <input
                          id="attr-filter-referrer-email"
                          type="email"
                          autoComplete="off"
                          placeholder="—"
                          value={attrFilterReferrerEmail}
                          onChange={(e) => setAttrFilterReferrerEmail(e.target.value)}
                          className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                      </div>
                      <div>
                        <label htmlFor="attr-filter-referred" className="text-xs font-medium text-zinc-500">
                          {a.filterReferredId}
                        </label>
                        <input
                          id="attr-filter-referred"
                          type="number"
                          inputMode="numeric"
                          placeholder="—"
                          value={attrReUid}
                          onChange={(e) => setAttrReUid(e.target.value)}
                          className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm tabular-nums dark:border-zinc-700 dark:bg-zinc-900"
                        />
                      </div>
                      <div>
                        <label htmlFor="attr-filter-referred-email" className="text-xs font-medium text-zinc-500">
                          {a.filterReferredEmail}
                        </label>
                        <input
                          id="attr-filter-referred-email"
                          type="email"
                          autoComplete="off"
                          placeholder="—"
                          value={attrFilterReferredEmail}
                          onChange={(e) => setAttrFilterReferredEmail(e.target.value)}
                          className="mt-1 w-full rounded-full border border-zinc-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setAttrRefUid('')
                          setAttrReUid('')
                          setAttrFilterReferrerEmail('')
                          setAttrFilterReferredEmail('')
                          setAttrSkip(0)
                        }}
                        className="rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
                      >
                        {a.attributionsResetFilters}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAttrSkip(0)
                          void fetchAttributionsList(0, {
                            referrerUserId: attrRefUid,
                            referredUserId: attrReUid,
                            referrerEmail: attrFilterReferrerEmail,
                            referredEmail: attrFilterReferredEmail,
                          })
                        }}
                        className="rounded-full border border-emerald-200 bg-emerald-50/80 px-4 py-2 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-100 dark:hover:bg-emerald-900/40"
                      >
                        {a.refresh}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-3 flex items-center gap-2">
                <List className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                  {a.attributionsListTitle}
                </h3>
              </div>
              {loading ? (
                <p className="text-sm text-zinc-500">{t.common.loading}</p>
              ) : (
                <div className="overflow-x-auto rounded-3xl border border-emerald-100/70 bg-white/90 shadow-md dark:border-emerald-900/50 dark:bg-zinc-950/90">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead className="border-b border-emerald-100 bg-emerald-50/50 dark:border-emerald-900/50 dark:bg-emerald-950/30">
                      <tr>
                        <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-200">id</th>
                        <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-200">{a.referrer}</th>
                        <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-200">{a.referred}</th>
                        <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-200">source</th>
                        <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-200">
                          {t.common.date}
                        </th>
                        <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-200">
                          {t.common.actions}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {attrs.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-10 text-center text-sm text-zinc-500">
                            {t.common.noResults}
                          </td>
                        </tr>
                      ) : (
                        attrs.map((x) => (
                          <tr
                            key={x.id}
                            className="border-b border-zinc-100/90 dark:border-zinc-800/80"
                          >
                            <td className="px-4 py-3 font-mono text-xs text-zinc-600">{x.id}</td>
                            <td className="px-4 py-3">
                              <div className="tabular-nums font-medium">{x.referrer_user_id}</div>
                              {x.referrer_email ? (
                                <div
                                  className="max-w-[220px] truncate text-xs text-zinc-500 dark:text-zinc-400"
                                  title={x.referrer_email}
                                >
                                  {x.referrer_email}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3">
                              <div className="tabular-nums font-medium">{x.referred_user_id}</div>
                              {x.referred_email ? (
                                <div
                                  className="max-w-[220px] truncate text-xs text-zinc-500 dark:text-zinc-400"
                                  title={x.referred_email}
                                >
                                  {x.referred_email}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3">
                              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs dark:bg-zinc-800">
                                {x.source}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-zinc-600">{x.created_at || '—'}</td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => void deleteAttr(x.id)}
                                className="rounded-full border border-red-200 px-3 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 dark:border-red-900/60 dark:hover:bg-red-950/40"
                              >
                                {t.common.delete}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
              <span>
                {a.total}: {attrTotal}
              </span>
              <button
                type="button"
                disabled={attrSkip < LIMIT}
                onClick={() => setAttrSkip((s) => Math.max(0, s - LIMIT))}
                className="rounded-full border border-zinc-300 px-3 py-1 disabled:opacity-40 dark:border-zinc-600"
              >
                {a.prev}
              </button>
              <button
                type="button"
                disabled={attrSkip + LIMIT >= attrTotal}
                onClick={() => setAttrSkip((s) => s + LIMIT)}
                className="rounded-full border border-zinc-300 px-3 py-1 disabled:opacity-40 dark:border-zinc-600"
              >
                {a.next}
              </button>
            </div>
          </div>
        )}

        {tab === 'commissions' && (
          <>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                {a.filterStatus}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-full border border-emerald-200 bg-white px-3 py-2 text-sm dark:border-emerald-800 dark:bg-zinc-900"
                >
                  <option value="">{a.allStatuses}</option>
                  <option value="payable">{a.payableOnly}</option>
                  <option value="pending_hold">{a.pendingHoldStatus}</option>
                  <option value="paid">paid</option>
                  <option value="clawed_back">clawed_back</option>
                </select>
              </label>
              <input
                type="number"
                placeholder={a.filterReferrerId}
                value={commReferrerId}
                onChange={(e) => setCommReferrerId(e.target.value)}
                className="rounded-full border px-3 py-2 text-sm dark:bg-zinc-900"
              />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-full border px-3 py-2 text-sm dark:bg-zinc-900"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-full border px-3 py-2 text-sm dark:bg-zinc-900"
              />
              <button
                type="button"
                onClick={() => {
                  setCommSkip(0)
                  void loadCommissions()
                }}
                className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-800 dark:border-emerald-700 dark:text-emerald-200"
              >
                {a.refresh}
              </button>
              <button
                type="button"
                onClick={() => void exportCsv()}
                className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-black hover:bg-emerald-400"
              >
                {a.exportCsv}
              </button>
            </div>
            <div className="mt-2 text-sm text-zinc-600">
              {a.total}: {commTotal}{' '}
              <button
                type="button"
                disabled={commSkip < LIMIT}
                onClick={() => setCommSkip((s) => Math.max(0, s - LIMIT))}
                className="ml-2 rounded-full border px-2 py-0.5 text-xs disabled:opacity-40"
              >
                {a.prev}
              </button>
              <button
                type="button"
                disabled={commSkip + LIMIT >= commTotal}
                onClick={() => setCommSkip((s) => s + LIMIT)}
                className="ml-1 rounded-full border px-2 py-0.5 text-xs disabled:opacity-40"
              >
                {a.next}
              </button>
            </div>

            {loading ? (
              <p className="mt-8 text-zinc-500">{t.common.loading}</p>
            ) : (
              <div className="mt-8 overflow-x-auto rounded-3xl border border-emerald-100/70 bg-white/90 shadow-lg dark:border-emerald-900/50 dark:bg-zinc-950/90">
                <table className="w-full min-w-[1000px] text-left text-sm">
                  <thead className="border-b border-emerald-100 dark:border-emerald-900/50">
                    <tr>
                      <th className="px-4 py-3">{t.common.date}</th>
                      <th className="px-4 py-3">{a.referrer}</th>
                      <th className="px-4 py-3">{a.referred}</th>
                      <th className="px-4 py-3">{a.invoice}</th>
                      <th className="px-4 py-3">{t.affiliate.amount}</th>
                      <th className="px-4 py-3">{t.common.status}</th>
                      <th className="px-4 py-3">{a.payableAt}</th>
                      <th className="px-4 py-3">{a.stripeTransfer}</th>
                      <th className="px-4 py-3">{a.markPaid}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.public_id} className="border-b border-zinc-100 dark:border-zinc-800">
                        <td className="px-4 py-3 text-zinc-600">{new Date(r.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3">{r.referrer_user_id}</td>
                        <td className="px-4 py-3">{r.referred_user_id}</td>
                        <td className="max-w-[140px] truncate px-4 py-3 font-mono text-xs">{r.stripe_invoice_id}</td>
                        <td className="px-4 py-3 font-medium">{fmt(r.commission_amount_cents, r.currency)}</td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs dark:bg-zinc-800">
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-600">
                          {r.payable_at ? new Date(r.payable_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{r.stripe_transfer_id || '—'}</td>
                        <td className="px-4 py-3">
                          {r.status === 'payable' ? (
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={!!stripeTransfer[r.public_id]}
                                  onChange={(e) =>
                                    setStripeTransfer((p) => ({ ...p, [r.public_id]: e.target.checked }))
                                  }
                                />
                                {a.markPaidStripe}
                              </label>
                              <input
                                type="text"
                                placeholder={a.payoutRefPlaceholder}
                                value={payoutRef[r.public_id] ?? ''}
                                onChange={(e) =>
                                  setPayoutRef((p) => ({ ...p, [r.public_id]: e.target.value }))
                                }
                                className="w-full min-w-[120px] rounded-full border border-zinc-200 px-3 py-1.5 text-xs dark:border-zinc-700 dark:bg-zinc-900"
                              />
                              <button
                                type="button"
                                disabled={marking === r.public_id}
                                onClick={() => void markPaid(r.public_id)}
                                className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                              >
                                {a.markPaid}
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-500">{r.payout_reference || '—'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
